@for (territory of territories; track territory.territoryName; let tIdx = $index) {
    <ntv-accordion
        size="md"
        variant="bordered"
        (accordionToggle)="onAccordionToggle(territory, $event)">
        <!-- Collapsed header -->
        <div slot="header" class="territory-item">
            <!-- Left: icon -->
            <div class="territory-item__icon">
                <svg width="16" height="16" class="territory-item__icon-svg">
                    <use xlink:href="assets/icons/icons-sprite.svg#map-folded"></use>
                </svg>
            </div>

            <!-- Middle: name + meta -->
            <div class="territory-item__info">
                <span class="territory-item__name">{{ territory.territoryName }}</span>
                <span class="territory-item__meta">
                    {{ territory.dealers.length }} dealer{{
                        territory.dealers.length !== 1 ? 's' : ''
                    }}
                    &nbsp;&bull;&nbsp;
                    {{ territory.host.length }} host{{ territory.host.length !== 1 ? 's' : '' }}
                </span>
            </div>

            <!-- Right: status + delete -->
            <div class="territory-item__actions">
                <span
                    class="territory-item__status"
                    [class.territory-item__status--active]="territory.status === 'Active'"
                    [class.territory-item__status--inactive]="territory.status === 'Inactive'"
                    [class.territory-item__status--pending]="territory.status === 'Pending'">
                    {{ territory.status }}
                </span>
                <button
                    class="territory-item__delete"
                    title="Delete"
                    (click)="openDeleteModal(territory.territoryName, $event)">
                    <svg width="14" height="14">
                        <use xlink:href="assets/icons/icons-sprite.svg#outline-trash"></use>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Expanded body -->
        <div slot="body" class="territory-body">
            <!-- Territory Info Section -->
            <div class="territory-body__section">
                <div class="territory-body__section-header">
                    <span class="territory-body__section-title">Territory Info</span>
                    @if (editingIndex() !== tIdx) {
                        <button
                            class="territory-body__edit-btn"
                            title="Edit"
                            (click)="startEdit(tIdx, territory, $event)">
                            <svg width="14" height="14">
                                <use xlink:href="assets/icons/icons-sprite.svg#pencil-edit"></use>
                            </svg>
                        </button>
                    } @else {
                        <div class="territory-body__edit-actions">
                            <button
                                class="territory-body__edit-save"
                                title="Save"
                                (click)="saveEdit(territory, $event)">
                                Save
                            </button>
                            <button
                                class="territory-body__edit-cancel"
                                title="Cancel"
                                (click)="cancelEdit($event)">
                                Cancel
                            </button>
                        </div>
                    }
                </div>

                <!-- View mode -->
                @if (editingIndex() !== tIdx) {
                    <div class="territory-body__grid">
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Territory Name</span>
                            <span class="territory-body__field-value">{{
                                territory.territoryName
                            }}</span>
                        </div>
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Longitude</span>
                            <span class="territory-body__field-value">{{
                                territory.longitude[0]
                            }}</span>
                        </div>
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Total Area</span>
                            <span class="territory-body__field-value"
                                >{{ territory.totalArea | number: '1.2-2' }} km²</span
                            >
                        </div>
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Latitude</span>
                            <span class="territory-body__field-value">{{
                                territory.latitude[0]
                            }}</span>
                        </div>
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Assigned Dealer</span>
                            <span class="territory-body__field-value">{{
                                territory.assignedDealer
                            }}</span>
                        </div>
                        <div class="territory-body__field">
                            <span class="territory-body__field-label">Created</span>
                            <span class="territory-body__field-value">{{
                                territory.createdDate | date: 'M/d/yyyy'
                            }}</span>
                        </div>
                    </div>
                }

                <!-- Edit mode -->
                @if (editingIndex() === tIdx) {
                    <div class="territory-body__edit-form">
                        <div class="territory-body__field">
                            <label class="territory-body__field-label">Territory Name</label>
                            <input
                                class="territory-body__input"
                                type="text"
                                [(ngModel)]="editNameValue"
                                (click)="$event.stopPropagation()" />
                        </div>
                        <div class="territory-body__field">
                            <label class="territory-body__field-label">Assigned Dealer</label>
                            <ntv-autocomplete
                                [options]="dealerOptions"
                                [config]="{
                                    placeholder: 'Search dealer...',
                                    searchable: true,
                                    clearable: true,
                                }"
                                (selectionChange)="onDealerChange($event)"
                                (click)="$event.stopPropagation()" />
                        </div>
                    </div>
                }
            </div>

            <!-- Host Section -->
            <div class="territory-body__section">
                <div class="territory-body__section-header">
                    <span class="territory-body__section-title">Host</span>
                </div>

                @for (host of territory.host; track host.hostName; let i = $index) {
                    @let hostKey = tIdx + '-' + i;
                    <div class="host-card" [class.host-card--expanded]="isHostExpanded(hostKey)">
                        <!-- Collapsed row -->
                        <div class="host-card__row" (click)="toggleHost(hostKey, $event)">
                            <svg
                                width="20"
                                height="24"
                                class="host-card__pin"
                                [class.host-card__pin--0]="i % 3 === 0"
                                [class.host-card__pin--1]="i % 3 === 1"
                                [class.host-card__pin--2]="i % 3 === 2">
                                <use xlink:href="assets/icons/icons-sprite.svg#location-pin"></use>
                            </svg>

                            <div class="host-card__info">
                                <div class="host-card__top">
                                    <span class="host-card__name">{{ host.hostName }}</span>
                                    <span class="host-card__status host-card__status--active"
                                        >Active</span
                                    >
                                    <span class="host-card__licenses"
                                        >{{ host.licenses.length }} License(s)</span
                                    >
                                    <svg
                                        width="12"
                                        height="12"
                                        class="host-card__chevron"
                                        [class.host-card__chevron--open]="isHostExpanded(hostKey)">
                                        <use
                                            xlink:href="assets/icons/icons-sprite.svg#chevron"></use>
                                    </svg>
                                </div>
                                <span class="host-card__address">{{ host.address }}</span>
                            </div>
                        </div>

                        <!-- Expanded details -->
                        @if (isHostExpanded(hostKey)) {
                            <div class="host-detail">
                                <div class="host-detail__columns">
                                    <!-- Left: Category + Licenses -->
                                    <div class="host-detail__left">
                                        <div class="host-detail__group">
                                            <span class="host-detail__label">Category:</span>
                                            <span class="host-detail__value">{{
                                                territory.category
                                            }}</span>
                                        </div>
                                        <div class="host-detail__group">
                                            <span class="host-detail__label">Licenses:</span>
                                            @for (
                                                license of host.licenses;
                                                track license;
                                                let li = $index
                                            ) {
                                                <div class="host-detail__license">
                                                    <span class="host-detail__license-dot"></span>
                                                    <span class="host-detail__value"
                                                        >License # {{ li + 1 }}</span
                                                    >
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <!-- Right: Operation Hours -->
                                    <div class="host-detail__right">
                                        <span class="host-detail__label">Operation Hours:</span>
                                        @for (
                                            entry of getOperationHours(host.operationHours);
                                            track entry.day
                                        ) {
                                            <div class="host-detail__hours-row">
                                                <span class="host-detail__day">{{
                                                    entry.day
                                                }}</span>
                                                <span class="host-detail__value"
                                                    >{{ entry.open }} - {{ entry.close }}</span
                                                >
                                            </div>
                                        }
                                    </div>
                                </div>

                                <!-- Coordinates -->
                                <div class="host-detail__coords">
                                    <svg width="14" height="14" class="host-detail__coords-pin">
                                        <use
                                            xlink:href="assets/icons/icons-sprite.svg#location-pin"></use>
                                    </svg>
                                    <span class="host-detail__value"
                                        >{{ host.latitude }}, {{ host.longitude }}</span
                                    >
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    </ntv-accordion>
}
